/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import type { VNode } from "preact";
import { useCallback, useState } from "preact/hooks";
import { AbsoluteTime, LoginTokenScope } from "@gnu-taler/taler-util";
import { useAccessTokens } from "../api/hooks.js";
import { currentAccount, currentBackendUrl, session } from "../stores/session.js";
import { AccessScreen } from "../screens/AccessScreen.js";
import type { PendingProtectedAction } from "../api/protectedAction.js";
import { runProtectedMutation } from "../api/contracts.js";
import { useTranslation } from "../context/translation.js";
import {
  checkPairingBackend,
  makePosPairingUri,
  pairingDuration,
  type IssuedPairingCredential,
  type PairingRequest,
} from "@gnu-taler/taler-util";

export interface AccessRouteProps {
  issuedPairingCredential: IssuedPairingCredential | null;
  onIssuedPairingCredentialChange: (credential: IssuedPairingCredential | null) => void;
  onMfaRequired: (pending: PendingProtectedAction) => void;
}

export function AccessRoute({
  issuedPairingCredential,
  onIssuedPairingCredentialChange,
  onMfaRequired,
}: AccessRouteProps): VNode {
  const { t } = useTranslation();
  const [query, setQuery] = useState<{ limit: number; offset?: string }>({ limit: 20 });
  const { tokens, resource, createToken, revokeToken, hasNextPage, lastUpdated } = useAccessTokens(query);
  const hasToken = Boolean(session.value.token);
  const backendUrl = currentBackendUrl.value;
  const instance = currentAccount.value || "default";
  const pairingCompatibility = checkPairingBackend(backendUrl);

  const pairingUnavailableReason = pairingCompatibility.compatible
    ? undefined
    : (() => {
        switch (pairingCompatibility.reason) {
          case "https":
            return t`Till pairing requires a merchant backend available through HTTPS.`;
          case "port":
            return t`Till pairing cannot represent a merchant backend on a custom port.`;
          case "path":
            return t`Till pairing cannot represent a merchant backend below a path prefix.`;
          case "query":
            return t`Till pairing cannot represent a merchant backend URL with a query.`;
          case "fragment":
            return t`Till pairing cannot represent a merchant backend URL with a fragment.`;
          case "invalid":
            return t`Till pairing requires a valid merchant backend URL.`;
        }
      })();

  const onQueryChange = useCallback((next: { limit: number; offset?: string }) => {
    setQuery((previous) =>
      previous.limit === next.limit && previous.offset === next.offset
        ? previous
        : next,
    );
  }, []);

  const finishPairingCreation = (request: PairingRequest, result: {
    token?: string;
    expiration?: Parameters<typeof AbsoluteTime.formatTimestamp>[0];
  }): void => {
    if (!result.token || !result.expiration) {
      throw new Error(t`The merchant backend did not return the issued PoS credential.`);
    }
    onIssuedPairingCredentialChange({
      deviceName: request.deviceName,
      pairingUri: makePosPairingUri(backendUrl, instance, result.token),
      expirationText: AbsoluteTime.formatTimestamp(result.expiration, ""),
    });
  };

  const createPairingToken = async (request: PairingRequest): Promise<void> => {
    if (!hasToken || !createToken || !pairingCompatibility.compatible) return;
    const tokenPayload = {
      description: t`Till: ${request.deviceName}`,
      scope: request.refreshable
        ? LoginTokenScope.OrderPos_Refreshable
        : LoginTokenScope.OrderPos,
      duration: pairingDuration(request.lifetime),
      password: request.password,
    };
    const result = await createToken(tokenPayload);
    if (result.mfaRequired && result.challengeResponse) {
      onMfaRequired({
        accountLabel: instance,
        actionNotice: t`Pairing till (${request.deviceName})`,
        challengeResponse: result.challengeResponse,
        cancelTo: "/access",
        continueWith: (challengeIds) =>
          runProtectedMutation(async () => {
            const continued = await createToken(tokenPayload, challengeIds);
            if (continued.mfaRequired && continued.challengeResponse) {
              return {
                type: "challenge" as const,
                challenge: continued.challengeResponse,
              };
            }
            finishPairingCreation(request, continued);
            return { redirectTo: "/access" };
          }),
        revalidate: resource.refresh,
      });
      return;
    }
    finishPairingCreation(request, result);
  };

  return (
    <AccessScreen
      tokens={hasToken ? (tokens ?? []) : undefined}
      tokensResource={hasToken ? resource : undefined}
      hasNextPage={hasToken ? hasNextPage : false}
      lastUpdated={hasToken ? lastUpdated : undefined}
      onRefresh={hasToken ? () => void resource.refresh() : undefined}
      onQueryChange={hasToken ? onQueryChange : undefined}
      onRevokeToken={hasToken ? async (id) => { if (revokeToken) await revokeToken(id); } : undefined}
      onCreatePairing={hasToken ? createPairingToken : undefined}
      pairingUnavailableReason={pairingUnavailableReason}
      issuedPairingCredential={issuedPairingCredential}
      onDismissPairing={() => {
        onIssuedPairingCredentialChange(null);
        void resource.refresh();
      }}
    />
  );
}
